import { configureStore, Middleware } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import { useDispatch } from 'react-redux';

import baseApi, {
  authApi,
  googleAuthApi,
  googleSpreadsheetsApi,
  noAuthBaseApi,
  redirectionApi
} from '@services/api';
import projectBoard from '@pages/ProjectBoard/slice';
import currentChartData from '@pages/EditChart/slice';
import currentChartPreviewData from '@pages/ChartPreview/slice';
import userDetails from '@components/AuthGuard/slice';
import loginData from '@pages/SignIn/slice';
import notificationsData from '@components/Notification/slice';
import RoutesPath from '@constants/RoutesPath';
import deletedProjectsState from '@pages/DeletedProjects/slice';

import AppData, { setApiErrorLog, setPlanUpdateErrors } from './AppSlice';
import { API_ERROR, PAGE_NOT_FOUND } from '@constants/eventNames';
import { sendAmplitudeData } from '@utils/amplitude';

export const rtkQueryErrorLogger: Middleware = () => (next) => (action) => {
  if (typeof action?.payload?.status === 'number') {
    const errorData = {
      endpoint:
        action?.meta?.baseQueryMeta?.request?.url?.replace(
          `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_API_VERSION}/`,
          ''
        ) || '',
      method: action?.meta?.baseQueryMeta?.request?.method || null,
      payload:
        { ...action?.meta?.arg?.originalArgs, password: undefined } || null,
      response: action?.payload?.data || null,
      errorStatus: action?.payload?.status || null
    };

    if (
      action?.payload?.status === 404 &&
      !action?.type.includes('googleSpreadsheetsApi')
    ) {
      sendAmplitudeData(PAGE_NOT_FOUND, errorData);
      window?.location?.replace(RoutesPath.PAGE_NOT_FOUND);
    } else {
      if (
        action?.payload?.status === 402 &&
        errorData?.response?.plan_details
      ) {
        store.dispatch(
          setPlanUpdateErrors({
            button_text: errorData.response.plan_details?.button_text,
            title: errorData.response.plan_details?.title,
            sub_title: errorData.response.plan_details?.sub_title,
            feature_name: errorData.response.plan_details?.feature_name,
            modal_name: errorData.response.plan_details?.modal_name
          })
        );
      } else {
        sendAmplitudeData(API_ERROR, errorData);
        store.dispatch(setApiErrorLog(errorData));
      }
    }
  } else if (action?.meta?.baseQueryMeta?.response?.status >= 400) {
    const errorData = {
      endpoint:
        action.meta.baseQueryMeta.request?.url?.replace(
          `${process.env.REACT_APP_API_DOMAIN}/${process.env.REACT_APP_API_VERSION}/`,
          ''
        ) || '',
      method: action.meta.baseQueryMeta.request?.method || null,
      payload:
        { ...action.meta.arg?.originalArgs, password: undefined } || null,
      response: action.meta.baseQueryMeta.response.statusText || null,
      errorStatus: action.meta.baseQueryMeta.response.status
    };

    sendAmplitudeData(API_ERROR, errorData);
    store.dispatch(setApiErrorLog(errorData));
  }

  return next(action);
};

export const store = configureStore({
  reducer: {
    [baseApi.reducerPath]: baseApi.reducer,
    [authApi.reducerPath]: authApi.reducer,
    [noAuthBaseApi.reducerPath]: noAuthBaseApi.reducer,
    [googleSpreadsheetsApi.reducerPath]: googleSpreadsheetsApi.reducer,
    [googleAuthApi.reducerPath]: googleAuthApi.reducer,
    [redirectionApi.reducerPath]: redirectionApi.reducer,
    AppData,
    projectBoard,
    currentChartData,
    currentChartPreviewData,
    userDetails,
    loginData,
    notificationsData,
    deletedProjectsState
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      baseApi.middleware,
      authApi.middleware,
      noAuthBaseApi.middleware,
      googleSpreadsheetsApi.middleware,
      googleAuthApi.middleware,
      redirectionApi.middleware,
      rtkQueryErrorLogger
    )
});

setupListeners(store.dispatch);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch = () => useDispatch<AppDispatch>();

export default store;
